import React, { useState, useEffect } from 'react';
import { userApiClient } from '../../api/HttpClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Trash2, UserPlus, Shield, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Member {
  member_id: string;
  user_id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  is_owner: boolean;
}

const ROLE_BADGE: Record<string, string> = {
  Admin:   'bg-primary/10 text-primary',
  Owner:   'bg-primary/10 text-primary',
  owner:   'bg-primary/10 text-primary',
  admin:   'bg-primary/10 text-primary',
  manager: 'bg-amber-100 text-amber-700',
  staff:   'bg-muted text-muted-foreground',
};

const TeamManagementTab: React.FC = () => {
  const [members, setMembers]     = useState<Member[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [showForm, setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess]     = useState<string | null>(null);

  const [name, setName]         = useState('');
  const [phone, setPhone]       = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole]         = useState('staff');

  const fetchTeam = async () => {
    setLoading(true);
    setError(null);
    try {
      const client = await userApiClient;
      const data = await client.get<{ team: Member[] }>('/api/users/my-team/');
      setMembers(data.team);
    } catch (e: any) {
      setError(e.message || 'Failed to load team.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeam(); }, []);

  const handleAddMember = async () => {
    setFormError(null);
    if (!name || !phone || !email || !password) {
      setFormError('All fields are required.');
      return;
    }
    setSubmitting(true);
    try {
      const client = await userApiClient;
      await client.post('/api/users/create-staff/', { name, phone, email, password, role });
      setSuccess(`${role === 'manager' ? 'Manager' : 'Staff'} account created successfully.`);
      setShowForm(false);
      setName(''); setPhone(''); setEmail(''); setPassword(''); setRole('staff');
      fetchTeam();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: any) {
      setFormError(e.message || 'Failed to create account.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Remove ${memberName} from your team?`)) return;
    try {
      const client = await userApiClient;
      await client.delete(`/api/users/remove-member/${memberId}/`);
      fetchTeam();
    } catch (e: any) {
      setError(e.message || 'Failed to remove member.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-bold text-foreground">Team Members</h2>
        </div>
        <Button
          size="sm"
          className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
          onClick={() => { setShowForm(!showForm); setFormError(null); }}
        >
          <UserPlus className="h-4 w-4" />
          Add Member
        </Button>
      </div>

      {/* Success banner */}
      {success && (
        <div className="bg-primary/10 text-primary border border-primary/20 rounded-lg px-4 py-3 text-sm font-medium">
          ✓ {success}
        </div>
      )}

      {/* Add member form */}
      {showForm && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="bg-primary px-4 py-3">
            <h3 className="text-primary-foreground font-semibold">New Team Member</h3>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Full Name *</Label>
              <Input placeholder="e.g. Ravi Kumar" value={name}
                onChange={e => setName(e.target.value)} className="bg-background" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Phone *</Label>
              <Input placeholder="10-digit mobile" value={phone}
                onChange={e => setPhone(e.target.value)} className="bg-background" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Email *</Label>
              <Input placeholder="email@example.com" type="email" value={email}
                onChange={e => setEmail(e.target.value)} className="bg-background" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Password *</Label>
              <Input placeholder="Set a password for them" type="password" value={password}
                onChange={e => setPassword(e.target.value)} className="bg-background" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Role *</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border z-50">
                  <SelectItem value="manager">Manager — full access</SelectItem>
                  <SelectItem value="staff">Staff — limited access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {formError && (
            <p className="px-4 pb-2 text-sm text-red-500">{formError}</p>
          )}
          <div className="px-4 pb-4 flex gap-3">
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={handleAddMember} disabled={submitting}>
              {submitting ? 'Creating...' : 'CREATE ACCOUNT'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setFormError(null); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Members list */}
      {loading ? (
        <p className="text-muted-foreground text-sm">Loading team...</p>
      ) : error ? (
        <p className="text-red-500 text-sm">{error}</p>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Phone</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={m.member_id}
                  className={cn('border-t border-border', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                  <td className="px-4 py-3 font-medium text-foreground">
                    <div className="flex items-center gap-2">
                      {m.is_owner && <Shield className="h-3.5 w-3.5 text-primary shrink-0" />}
                      {m.name || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{m.phone || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className={cn('px-2 py-1 rounded-full text-xs font-semibold capitalize',
                      ROLE_BADGE[m.role] ?? 'bg-muted text-muted-foreground')}>
                      {m.is_owner ? 'Owner' : m.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!m.is_owner && (
                      <Button variant="ghost" size="icon"
                        className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRemove(m.member_id, m.name)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                    No team members yet. Add a manager or staff member above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Login instructions */}
      {members.length > 1 && (
        <div className="bg-muted/40 border border-border rounded-xl p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">How team members log in</p>
          <p>Share their email + password with them. They log in at the same URL and see your full dashboard automatically.</p>
        </div>
      )}
    </div>
  );
};

export default TeamManagementTab;
